import React, { useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import { Table, Button, Form, Modal, InputGroup, Pagination, Badge, Spinner } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import "./AdminRolesPage.css";
import { getSerialNumber } from "../../utils/pagination";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";

// Agar Admin CSS globally call nahi ho rahi, to apna exact css path import karo
// import "./Admin.css";

export default function AdminRolesPage() {
  const [roles, setRoles] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);

  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [name, setName] = useState("");
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/roles`);
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch Roles Error:", err);
      toast.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = useMemo(() => {
    return roles.filter((role) =>
      role.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [roles, search]);

  const totalPages = Math.ceil(filteredRoles.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRoles = filteredRoles.slice(indexOfFirst, indexOfLast);

  const openAddModal = () => {
    setEditId(null);
    setName("");
    setModalShow(true);
  };

  const handleEdit = (role) => {
    setEditId(role.id);
    setName(role.name || "");
    setModalShow(true);
  };

  const closeModal = () => {
    setModalShow(false);
    setEditId(null);
    setName("");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning("Role name is required");
      return;
    }

    try {
      setSaving(true);

      if (editId) {
        await api.put(`/roles/${editId}`, {
          name: name.trim(),
        });
        toast.success("Role updated successfully");
      } else {
        await api.post(`/roles`, {
          name: name.trim(),
        });
        toast.success("Role added successfully");
      }

      closeModal();
      fetchRoles();
    } catch (err) {
      console.error("Save Role Error:", err);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setDeleteModalShow(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setDeleteModalShow(false);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/roles/${deleteId}`);
      toast.success("Role deleted successfully");
      closeDeleteModal();
      fetchRoles();
    } catch (err) {
      console.error("Delete Role Error:", err);
      toast.error("Failed to delete role");
    }
  };

  const exportCSV = () => {
    if (!filteredRoles.length) {
      toast.warning("No data available to export");
      return;
    }

    const headers = ["ID", "Role Name"];
    const rows = filteredRoles.map((role) => [role.id, role.name]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "roles.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="unit-page roles-mgmt-theme">

      <div className="unit-header">
        <div>
          <div className="unit-page-label">Configuration</div>
          <h2>Role Management</h2>
          <p>Manage system roles, permissions grouping and user access levels.</p>
        </div>

        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={fetchRoles}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>

          <Button className="unit-btn unit-btn-light" onClick={exportCSV}>
            <i className="fa-solid fa-file-export" aria-hidden="true"></i>
            Export CSV
          </Button>

          <Can moduleKey="roles" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openAddModal}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add Role
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="roles-stat-icon roles-stat-icon-primary">
            <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
          </div>
          <div className="roles-stat-text">
            <span>Total Roles</span>
            <strong>{roles.length}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="roles-stat-icon roles-stat-icon-accent">
            <i className="fa-solid fa-filter" aria-hidden="true"></i>
          </div>
          <div className="roles-stat-text">
            <span>Filtered Result</span>
            <strong>{filteredRoles.length}</strong>
          </div>
        </div>

        <div className="unit-stat-card unit-stat-card--secondary">
          <div className="roles-stat-icon roles-stat-icon-muted">
            <i className="fa-solid fa-list-ol" aria-hidden="true"></i>
          </div>
          <div className="roles-stat-text">
            <span>Current Page</span>
            <strong>{currentPage}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-user-shield roles-list-icon" aria-hidden="true"></i>
              Role List
            </h5>
            <p>View, search, add, update and delete roles.</p>
          </div>

          <div className="unit-toolbar-actions">
            <InputGroup className="unit-search">
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by role name..."
                aria-label="Search by role name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <Button
                  className="unit-clear-btn"
                  onClick={() => setSearch("")}
                >
                  Clear
                </Button>
              )}
            </InputGroup>

            <Form.Select
              className="unit-page-size"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </Form.Select>
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading roles...</p>
            </div>
          ) : (
            <Table responsive className="unit-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>S.NO.</th>
                  <th>Role Name</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentRoles.length > 0 ? (
                  currentRoles.map((role, index) => (
                    <tr key={role.id}>
                      <td>{getSerialNumber(index, currentPage, rowsPerPage)}</td>

                      <td>
                        <div className="unit-name-box">
                          <div className="unit-letter">
                            {role.name?.charAt(0)?.toUpperCase()}
                          </div>

                          <div>
                            <strong>{role.name}</strong>
                            <small>Role ID: {role.id}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <Badge className="unit-status">Active</Badge>
                      </td>

                      <td>
                        <div className="unit-actions">
                          <Can moduleKey="roles" actionKey="edit">
                            <Button
                              className="unit-icon-btn unit-edit"
                              title="Edit Role"
                              aria-label={`Edit ${role.name || "role"}`}
                              onClick={() => handleEdit(role)}
                            >
                              <i className="fa-solid fa-pen" aria-hidden="true"></i>
                            </Button>
                          </Can>

                          <Can moduleKey="roles" actionKey="delete">
                            <Button
                              className="unit-icon-btn unit-delete"
                              title="Delete Role"
                              aria-label={`Delete ${role.name || "role"}`}
                              onClick={() => openDeleteModal(role.id)}
                            >
                              <i className="fa-solid fa-trash" aria-hidden="true"></i>
                            </Button>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
                        </div>
                        <h5>No roles found</h5>
                        <p>Try another keyword or add a new role.</p>

                        <Button
                          className="unit-btn unit-btn-primary"
                          onClick={openAddModal}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          Add Role
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>{filteredRoles.length === 0 ? 0 : indexOfFirst + 1}</strong>{" "}
            to <strong>{Math.min(indexOfLast, filteredRoles.length)}</strong>{" "}
            of <strong>{filteredRoles.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              />

              {compactPageItems([...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              )), currentPage)}

              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              />
            </Pagination>
          )}
        </div>
      </div>

      <Modal
        show={modalShow}
        onHide={closeModal}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <ShieldOutlinedIcon className="modal-header-icon" aria-hidden="true" />
            <Modal.Title>{editId ? "Edit Role" : "Add New Role"}</Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Role Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Example: Admin"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeModal}>
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : editId ? "Update Role" : "Create Role"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteModalShow}
        onHide={closeDeleteModal}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <Modal.Title>Delete Role?</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="admin-modal-danger-icon" aria-hidden="true">
            <DeleteOutlineIcon fontSize="inherit" />
          </div>
          <p className="unit-delete-text text-center">
            Are you sure you want to delete this role? This action cannot be undone.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeDeleteModal}>
            Cancel
          </Button>

          <Button className="unit-btn unit-btn-danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}